import { Component, Inject } from "@angular/core"
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, NgForm, Validators } from "@angular/forms"
import { ErrorStateMatcher } from "@angular/material/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog"
import { SupportTicket, TICKET_CONTEXT_MAX, TICKET_DESCRIPTION_MAX, TICKET_DESCRIPTION_MIN, TICKET_PRIORITY, TICKET_SUBJECT_MAX, TICKET_SUBJECT_MIN, TICKET_TYPE, getTicketPriorityName } from "../../common/user"
import { UserService } from "../user.service";
import { environment } from "../../../environments/environment";

export class MyErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
      const isSubmitted = form && form.submitted;
      return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
    }
}

// the types a user raises; leads come from elsewhere
const TYPES = [
    { value: TICKET_TYPE.QUESTION, hint: 'How to do something' },
    { value: TICKET_TYPE.INCIDENT, hint: 'Something is broken' },
    { value: TICKET_TYPE.PROBLEM, hint: 'Something keeps going wrong' },
    { value: TICKET_TYPE.FEATURE, hint: 'An idea for LINQ' },
]

const PRIORITIES = [
    { value: TICKET_PRIORITY.LOW, hint: 'No rush' },
    { value: TICKET_PRIORITY.MEDIUM, hint: 'There is a workaround' },
    { value: TICKET_PRIORITY.HIGH, hint: 'Blocking important work' },
    { value: TICKET_PRIORITY.URGENT, hint: 'LINQ is unusable' },
]

interface TicketDraft {
    type:TICKET_TYPE
    priority:TICKET_PRIORITY
    subject:string
    description:string
    includeContext:boolean
}

// what was typed into a ticket that hasn't been sent, so closing the dialog or a failed send doesn't lose it
let draft:TicketDraft|undefined

// called once a ticket has been sent
export const clearTicketDraft = () => { draft = undefined }

@Component({
    selector: 'support-ticket-dialog',
    templateUrl: 'ticket-dialog.html',
    styleUrls: ['ticket-dialog.css'],
    standalone: false
})
export class TicketDialogComponent {
    ticketForm: FormGroup
    matcher = new MyErrorStateMatcher();
    types = TYPES
    priorities = PRIORITIES
    email:string
    TICKET_SUBJECT_MIN = TICKET_SUBJECT_MIN
    TICKET_SUBJECT_MAX = TICKET_SUBJECT_MAX
    TICKET_DESCRIPTION_MIN = TICKET_DESCRIPTION_MIN
    TICKET_DESCRIPTION_MAX = TICKET_DESCRIPTION_MAX
    restoredDraft = false
    context:string

    constructor(public dialogRef: MatDialogRef<TicketDialogComponent, SupportTicket>, private formBuilder: FormBuilder, userService:UserService,
        @Inject(MAT_DIALOG_DATA) public data: SupportTicket) {

        this.email = userService.getUser()?.email
        this.context = this.getContext()
        this.ticketForm  =  this.formBuilder.group({
            type: [this.data.type || TICKET_TYPE.QUESTION, [Validators.required]],
            priority: [this.data.priority || TICKET_PRIORITY.LOW, [Validators.required]],
            subject: [this.data.subject, [Validators.required, Validators.minLength(TICKET_SUBJECT_MIN), Validators.maxLength(TICKET_SUBJECT_MAX)]],
            description: [this.data.description, [Validators.required, Validators.minLength(TICKET_DESCRIPTION_MIN), Validators.maxLength(TICKET_DESCRIPTION_MAX)]],
            includeContext: [true],
        })

        // a new ticket picks up where an unsent one left off
        if (draft && !this.data.subject && !this.data.description) {
            this.ticketForm.patchValue(draft)
            this.restoredDraft = true
        }

        // keep what was typed whenever the dialog closes; the caller clears it once the ticket is sent
        dialogRef.beforeClosed().subscribe(() => {
            const values = this.ticketForm.value
            if ((values.subject || '').trim() || (values.description || '').trim()) draft = {...values}
        })
    }

    // where the ticket is raised from, to help support reproduce a problem
    private getContext() {
        const page = (location.hash || '').replace(/^#/, '') || '/'
        return [
            `Page: ${page}`,
            `LINQ version: ${environment.version}`,
            `Browser: ${navigator.userAgent}`,
            `Screen: ${window.innerWidth}x${window.innerHeight}`,
        ].join('\n').slice(0, TICKET_CONTEXT_MAX)
    }

    clearDraft() {
        draft = undefined
        this.restoredDraft = false
        this.ticketForm.reset({type:TICKET_TYPE.QUESTION, priority:TICKET_PRIORITY.LOW, subject:'', description:'', includeContext:true})
    }

    // closes with a new ticket; the caller sends it
    save() {
        this.ticketForm.markAllAsTouched()
        if (this.ticketForm.invalid) return

        const values = this.ticketForm.value
        this.dialogRef.close({
            ...this.data,
            type: values.type,
            priority: values.priority,
            subject: values.subject.trim(),
            description: values.description.trim(),
            context: values.includeContext ? this.context : undefined,
        } as SupportTicket);
    }

    onCancelClick(): void {
        this.dialogRef.close();
    }

    getPriorityName(priority:TICKET_PRIORITY) {
        return getTicketPriorityName(priority)
    }

    getTypeHint() {
        return TYPES.find(t => t.value === this.ticketForm.value.type)?.hint
    }

    getPriorityHint() {
        return PRIORITIES.find(p => p.value === this.ticketForm.value.priority)?.hint
    }

    getSubjectError() {
        return `Subject must be between ${TICKET_SUBJECT_MIN} and ${TICKET_SUBJECT_MAX} characters`
    }

    getDescriptionError() {
        return `Description must be between ${TICKET_DESCRIPTION_MIN} and ${TICKET_DESCRIPTION_MAX} characters`
    }
}
